import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { postbackOutcomeLabel, postbackReasonText } from '../lib/postbackReasons';
import ConversionCheck from './ConversionCheck';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

/*
  Incoming postbacks of a campaign (plan: stage 3, item 1): what the affiliate
  network sent, whether the tracker accepted it, and why not in plain words.
*/

function badgeVariant(outcome) {
  if (outcome === 'accepted') return 'default';
  if (outcome === 'ignored') return 'secondary';
  return 'destructive';
}

export default function IncomingPostbacks({ campaignId }) {
  const { t } = useT();
  const [items, setItems] = useState(null);
  const [checkOpen, setCheckOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/postback-inbound`);
      setItems(res.data?.items || []);
    } catch (_e) {
      setItems([]);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card data-testid="incoming-postbacks">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>{t('Incoming postbacks')}</CardTitle>
            <CardDescription>
              {t('What the affiliate network sent for this campaign in the last 30 days, and what the tracker did with it.')}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setCheckOpen(true)}>
              {t('Check click and conversion')}
            </Button>
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCw className="mr-1 h-4 w-4" />
              {t('Refresh')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items === null ? null : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('No postbacks from the network yet. If the network has already sent one, check that its address points to this tracker and carries the click ID.')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Time')}</TableHead>
                <TableHead>{t('Click ID')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
                <TableHead className="text-right">{t('Payout')}</TableHead>
                <TableHead>{t('Result')}</TableHead>
                <TableHead>{t('Explanation')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {row.received_at ? new Date(row.received_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.click_id || '—'}</TableCell>
                  <TableCell className="text-xs">{row.raw_status || '—'}</TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {row.revenue === null || row.revenue === undefined ? '—' : row.revenue}
                  </TableCell>
                  <TableCell>
                    <Badge variant={badgeVariant(row.outcome)}>{postbackOutcomeLabel(t, row.outcome)}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {postbackReasonText(t, row.reason)}
                    {row.detail ? <span className="block text-muted-foreground">{row.detail}</span> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <ConversionCheck
        campaignId={campaignId}
        open={checkOpen}
        onOpenChange={(value) => {
          setCheckOpen(value);
          if (!value) load();
        }}
      />
    </Card>
  );
}
